import { State, Action, StateContext, Selector, Store } from '@ngxs/store';
import { HistoryStateModel } from './history.state.model';
import { 
  AddHistory,
  Undo,
  Redo,
  ResetHistory,
  CacheChapterSynchronization,
  RevertChapterSynchronization,
  ClearHistory,
  RemoveChapterSynchronization,
  CopyChapterSynchronization 
} from './history.actions';
import { HistoryAction } from '../models/history-action.enum';
import { ViewTreeNode } from 'src/app/engine/models/view.tree-node';
import { ToggleVisibleNodes } from 'src/app/engine/state/nodes/engine.nodes.actions';
import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';
import { EngineService } from 'src/app/engine/engine.service';
import { SetChapter, RecordShadowChapter } from 'src/app/story/state/chapters/story.chapters.actions';
import { ChapterService } from 'src/app/story/chapter.service';

const initialState = {
  histories: [],
  current: -1,
  cache: {}
};

@State<HistoryStateModel>({
  name: 'history',
  defaults: initialState
})
export class HistoryState {
  /**
   * Selectors
   */
  @Selector()
  static getHistories(state: HistoryStateModel) {
    return state.histories;
  }

  @Selector()
  static getCurrent(state: HistoryStateModel) {
    return state.current;
  }

  /**
   * Actions
   */
  @Action(AddHistory)
  addHistory(context: StateContext<HistoryStateModel>, action: AddHistory) {
    // tslint:disable-next-line:prefer-const
    let { current, histories } = context.getState();

    // When recording history, eliminate all future events
    histories = histories.slice(0, current + 1);

    // Limit history to 100 entries to prevent memory overflow
    if (histories.length >= 100) {
      histories.unshift();
    }

    histories.push(action.history);
    
    // Update current index
    current = histories.length - 1;
    
    context.patchState({
      histories: [...histories],
      current: current
    });
  }

  @Action(Redo)
  redo(context: StateContext<HistoryStateModel>, action: Redo) {
    const histories = [...context.getState().histories];
    const current = context.getState().current + 1;
    const history = histories[current];

    if (!history) {
      return;
    }

    switch (history.action) {
      case HistoryAction.SetNodeVisible:
        this.toggleVisibleNodes(context, history);
        break;
    }

    context.patchState({
      current: current
    });
  }

  @Action(Undo)
  undo(context: StateContext<HistoryStateModel>, action: Undo) {
    const histories = [...context.getState().histories];
    const current = context.getState().current - 1;

    // No undos
    if (!histories.length || current < 0) {
      return;
    }

    const history = histories[current];

    switch (history.action) {
      case HistoryAction.SetNodeVisible:
        this.toggleVisibleNodes(context, history);
        
        break;
    }

    context.patchState({
      current: current
    });
  }

  @Action(ResetHistory)
  resetHistory(context: StateContext<HistoryStateModel>, action: ResetHistory) {
    context.setState(initialState);
  }

  @Action(ClearHistory)
  ClearHistory(context: StateContext<HistoryStateModel>, action: ClearHistory) {
    context.patchState({
      histories: [],
      current: -1,
    });
  }

  toggleVisibleNodes(context, history) {
    const nodes = <ViewTreeNode[]>Object.values(history.value);

    const visibleNodes = nodes
      .filter(node => node.boolState.shown);

    const hiddenNodes = nodes
      .filter(node => !node.boolState.shown);


    context.dispatch(new ToggleVisibleNodes(visibleNodes, true, false));
    context.dispatch(new ToggleVisibleNodes(hiddenNodes, false, false));
  }

  @Action(CacheChapterSynchronization)
  cacheChapterSynchronization(context: StateContext<HistoryStateModel>, action: CacheChapterSynchronization) {
    const { chapterId, cacheId } = action;
    const cacheKey = this.getCacheKey(cacheId, chapterId);
    const cache = context.getState().cache;

    // When readOnly is flagged, don't overwrite pre-existing values
    if (action.readOnly) {
      const synchronization = cache[cacheKey];
      if (synchronization) {
        return;
      }
    }

    cache[cacheKey] = {...action.synchronization};

    context.patchState({
      cache: {...cache}
    });
  }

  @Action(RevertChapterSynchronization)
  revertChapterSynchronization(context: StateContext<HistoryStateModel>, action: RevertChapterSynchronization) {
    // Access the cache
    const { chapterId, cacheId } = action;
    const synchronization = this.getSynchronization(context, cacheId, chapterId);

    if (synchronization) {
      // Revert the chapter synchronization
      let chapter = this.store.selectSnapshot(StoryChaptersState.getStoryChapters)
        .find(_chapter => _chapter.id === chapterId);
  
      chapter = {...chapter};
      chapter.synchronization = synchronization;
  
      // Update the engine, which should then propagate back to the other states
      this.engineService.setGraphState(chapter.synchronization);

      // Update state and engine
      const index = this.store.selectSnapshot(StoryChaptersState.getStoryChapters)
        .findIndex(_chapter =>  chapter.id === _chapter.id);

      this.store.dispatch(new SetChapter(chapter));
      this.chapterService.addChapterToEngine(chapter, index);
    }
  }

  @Action(RemoveChapterSynchronization)
  removeChapterSynchronization(context: StateContext<HistoryStateModel>, action: RemoveChapterSynchronization) {
    const cacheKey = this.getCacheKey(action.cacheId, action.chapterId);
    const cache = {...context.getState().cache};
    delete cache[cacheKey];

    context.patchState({
      cache: {...cache}
    });
  }

  @Action(CopyChapterSynchronization)
  copyChapterSynchronization(context: StateContext<HistoryStateModel>, action: CopyChapterSynchronization) {
    const synchronization = this.getSynchronization(context, action.sourceCacheId, action.sourceChapterId);
    context.dispatch(new CacheChapterSynchronization(action.targetCacheId, action.targetChapterId, synchronization));
  }

  private getSynchronization(context, cacheId, chapterId) {
    const cacheKey = this.getCacheKey(cacheId, chapterId);
    const cache = context.getState().cache;
    return cache[cacheKey];
  }

  private getCacheKey(cacheId, chapterId) {
    return `${cacheId}_${chapterId}`;
  }

  constructor(
    private store: Store,
    private engineService: EngineService,
    private chapterService: ChapterService
  ) { }
}
